/**
 * Consent gate.
 *
 * The pipeline will not start until a consent record exists on the device that (a)
 * acknowledges the current monitoring policy version and (b) covers every signal family the
 * agent is configured to collect.
 *
 * This is the enforcement point for §3 and §25. It is intentionally awkward to bypass: there
 * is no "skip consent" flag and no hidden mode, and the check runs before the capture layer
 * is constructed rather than alongside it.
 *
 * Note the deliberate limits, so the mechanism is not oversold. A stored record proves that
 * someone acknowledged a notice on this device. It does not establish lawful basis,
 * organisation-level notification, or freely given agreement — those are obligations of the
 * deploying organisation, and no software control can discharge them.
 *
 * Storage is abstracted behind `ConsentStore` because the host differs (a file in Electron,
 * `localStorage` in a browser pilot, a keychain entry later). The *rules* live here so they
 * are identical everywhere and testable in isolation.
 */

import { ConsentError } from '../errors.js';
import { undocumentedFamilies } from './manifest.js';

export const CONSENT_RECORD_VERSION = 1;

export interface ConsentRecord {
  readonly recordVersion: number;
  readonly policyVersion: string;
  readonly acknowledgedAt: Date;
  readonly acknowledgedBy: string;
  readonly deviceId: string;
  readonly agentVersion: string;
  readonly signals: readonly string[];
  readonly noticeDigest: string;
}

/** Host-provided persistence. Implementations must be synchronous-safe to call at startup. */
export interface ConsentStore {
  read(): Promise<unknown | null>;
  write(record: Record<string, unknown>): Promise<void>;
  clear(): Promise<void>;
  /** Where the record lives, for error messages. */
  readonly location: string;
}

export function consentToJson(record: ConsentRecord): Record<string, unknown> {
  return {
    record_version: record.recordVersion,
    policy_version: record.policyVersion,
    acknowledged_at: record.acknowledgedAt.toISOString(),
    acknowledged_by: record.acknowledgedBy,
    device_id: record.deviceId,
    agent_version: record.agentVersion,
    signals: [...record.signals],
    notice_digest: record.noticeDigest,
  };
}

export function consentFromJson(data: unknown): ConsentRecord {
  if (typeof data !== 'object' || data === null) {
    throw new ConsentError('Consent record is not an object');
  }
  const record = data as Record<string, unknown>;
  const acknowledgedAt = new Date(String(record.acknowledged_at));
  if (Number.isNaN(acknowledgedAt.getTime())) {
    throw new ConsentError(
      'Consent record is unreadable (bad or missing acknowledged_at). Re-acknowledge the ' +
        'monitoring notice before monitoring can start.',
    );
  }
  return {
    recordVersion: Number(record.record_version ?? CONSENT_RECORD_VERSION),
    policyVersion: String(record.policy_version ?? ''),
    acknowledgedAt,
    acknowledgedBy: String(record.acknowledged_by ?? ''),
    deviceId: String(record.device_id ?? ''),
    agentVersion: String(record.agent_version ?? ''),
    signals: Array.isArray(record.signals) ? record.signals.map(String) : [],
    noticeDigest: String(record.notice_digest ?? ''),
  };
}

export async function loadConsent(store: ConsentStore): Promise<ConsentRecord | null> {
  const raw = await store.read();
  if (raw === null || raw === undefined) return null;
  return consentFromJson(raw);
}

export interface RecordConsentInput {
  policyVersion: string;
  signals: readonly string[];
  agentVersion: string;
  acknowledgedBy: string;
  noticeDigest?: string;
  deviceId?: string;
  now?: Date;
}

/**
 * Write a consent acknowledgement for this device.
 *
 * The device id is a random identifier generated once and reused. It is deliberately not
 * derived from hardware or user identifiers: the backend needs a stable handle for the
 * device, and does not need a fingerprint of it.
 */
export async function recordConsent(
  store: ConsentStore,
  input: RecordConsentInput,
): Promise<ConsentRecord> {
  let existing: ConsentRecord | null = null;
  try {
    existing = await loadConsent(store);
  } catch {
    // A corrupt record is replaced by this acknowledgement, which is the point of the call.
    existing = null;
  }

  const record: ConsentRecord = {
    recordVersion: CONSENT_RECORD_VERSION,
    policyVersion: input.policyVersion,
    acknowledgedAt: input.now ?? new Date(),
    acknowledgedBy: input.acknowledgedBy,
    deviceId: input.deviceId ?? existing?.deviceId ?? generateDeviceId(),
    agentVersion: input.agentVersion,
    signals: [...input.signals],
    noticeDigest: input.noticeDigest ?? '',
  };

  await store.write(consentToJson(record));
  return record;
}

export interface RequireConsentInput {
  policyVersion: string;
  signals: Iterable<string>;
  now?: Date;
}

/**
 * Return the valid consent record, or throw explaining what is missing.
 *
 * Four failures are reported separately, because the remedy differs in each case: never
 * enrolled, policy superseded, collection scope expanded, and an implausible timestamp.
 */
export async function requireConsent(
  store: ConsentStore,
  input: RequireConsentInput,
): Promise<ConsentRecord> {
  const configured = [...input.signals];
  const record = await loadConsent(store);

  if (record === null) {
    throw new ConsentError(
      'Monitoring has not been acknowledged on this device.\n' +
        'Review the monitoring notice and record acknowledgement before starting.\n' +
        `Expected consent record at: ${store.location}`,
    );
  }

  if (record.policyVersion !== input.policyVersion) {
    throw new ConsentError(
      'The monitoring policy has changed since it was acknowledged on this device ' +
        `(acknowledged ${JSON.stringify(record.policyVersion)}, configured ` +
        `${JSON.stringify(input.policyVersion)}).\n` +
        'Re-acknowledge after reviewing the updated notice. Monitoring does not resume on a ' +
        'superseded acknowledgement.',
    );
  }

  const undeclared = configured.filter((family) => !record.signals.includes(family)).sort();
  if (undeclared.length > 0) {
    throw new ConsentError(
      'The agent is configured to collect signals that were not part of the acknowledged ' +
        `notice: ${undeclared.join(', ')}.\n` +
        'Expanding collection scope requires a fresh acknowledgement.',
    );
  }

  const undocumented = undocumentedFamilies(configured);
  if (undocumented.length > 0) {
    throw new ConsentError(
      'These configured signals have no published description in the data-collection ' +
        `manifest: ${undocumented.join(', ')}.\n` +
        'A signal that cannot be explained to the employee must not be collected. Add a ' +
        'SignalDescription in privacy/manifest.ts or remove the signal from ' +
        'privacy.collectSignals.',
    );
  }

  // Tolerate modest clock skew, but reject a record claiming to be from the future by more
  // than a day: that usually means it was hand-edited.
  const reference = input.now ?? new Date();
  if (record.acknowledgedAt.getTime() - reference.getTime() > 86_400_000) {
    throw new ConsentError(
      `Consent record is dated in the future (${record.acknowledgedAt.toISOString()}). ` +
        'Refusing to start until it is re-created.',
    );
  }

  return record;
}

/**
 * Delete the consent record. Returns true if one was removed.
 *
 * Revocation stops the agent at its next start. It does not delete already-collected data —
 * that is a backend retention/erasure concern, and implying otherwise here would be
 * misleading.
 */
export async function revokeConsent(store: ConsentStore): Promise<boolean> {
  const existing = await store.read();
  if (existing === null || existing === undefined) return false;
  await store.clear();
  return true;
}

/** In-memory store, for tests and for previewing the flow without persisting anything. */
export class MemoryConsentStore implements ConsentStore {
  readonly location = 'memory://consent';
  private data: Record<string, unknown> | null;

  constructor(initial: Record<string, unknown> | null = null) {
    this.data = initial;
  }

  async read(): Promise<unknown | null> {
    return this.data;
  }

  async write(record: Record<string, unknown>): Promise<void> {
    this.data = record;
  }

  async clear(): Promise<void> {
    this.data = null;
  }
}

function generateDeviceId(): string {
  const cryptoObject = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (cryptoObject?.randomUUID !== undefined) return cryptoObject.randomUUID();
  // Deterministically-shaped fallback for runtimes without WebCrypto.
  const random = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${random()}${random()}-${random()}-${random()}-${random()}-${random()}${random()}${random()}`;
}
